import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { Button, DatePicker } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type RoomItem = { id: string; name: string };

export type RoomUsageReportSearch = {
  dateFrom?: string;
  dateTo?: string;
  roomId?: string;
};

export function RoomUsageReportFilter() {
  const nav = useNavigate({ from: "/app/reports/room-usage" });
  const search = useSearch({
    from: "/app/reports/room-usage",
  }) as RoomUsageReportSearch;

  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");

  const [fromDate, setFromDate] = useState(search.dateFrom ?? defaultFrom);
  const [toDate, setToDate] = useState(search.dateTo ?? defaultTo);
  const [roomId, setRoomId] = useState(search.roomId ?? "");

  useEffect(() => {
    setFromDate(search.dateFrom ?? defaultFrom);
    setToDate(search.dateTo ?? defaultTo);
    setRoomId(search.roomId ?? "");
  }, [search.dateFrom, search.dateTo, search.roomId]);

  const hasFilter = roomId || search.dateFrom || search.dateTo;

  const applyFilters = (updates: Partial<RoomUsageReportSearch>) => {
    const next = {
      dateFrom: updates.dateFrom ?? fromDate,
      dateTo: updates.dateTo ?? toDate,
      roomId:
        updates.roomId !== undefined
          ? updates.roomId
          : roomId
            ? roomId
            : undefined,
    };
    nav({ search: next });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : defaultFrom;
              const toStr = to ? format(to, "yyyy-MM-dd") : defaultTo;
              setFromDate(fromStr);
              setToDate(toStr);
              applyFilters({ dateFrom: fromStr, dateTo: toStr });
            }}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />

          <InfiniteCombobox<RoomItem>
            queryKey={["master", "rooms", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<RoomItem>(
                `${config.apiUrl}/master/rooms/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<RoomItem>(
                `${config.apiUrl}/master/rooms/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={roomId}
            onValueChange={(val) => {
              setRoomId(val);
              applyFilters({ roomId: val || undefined });
            }}
            placeholder="ຫ້ອງ (ທຸກຫ້ອງ)"
            className="h-8 w-full sm:w-48"
            clearable
          />
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setRoomId("");
              setFromDate(defaultFrom);
              setToDate(defaultTo);
              nav({
                search: {
                  dateFrom: defaultFrom,
                  dateTo: defaultTo,
                },
              });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="size-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
